import React, { useState, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from 'react-apollo';
import produce from 'immer';
import { useHistory } from 'react-router';
import ReactTooltip from 'react-tooltip';

import DropdownMenu from '../../../../../../components/global/DropdownMenu';
import Button from '../../../../../../components/global/Button';
import DotTrigger from '../../../../../../components/global/DotTrigger';
import ContributeButton from '../../../../../../components/global/ContributeButton';
import InviteIcon from '../../../../../../components/icons/InviteIcon';
import UploadIcon from '../../../../../../components/icons/UploadIcon';
import SlideshowIcon from '../../../../../../components/icons/SlideshowIcon';
import NotificationIcon from '../../../../../../components/icons/NotificationIcon';
import TrashIcon from '../../../../../../components/icons/TrashIcon';
import MyMatchesIcon from '../../../../../../components/icons/MyMatchesIcon';
import DraftWaldogramsIcon from '../../../../../../components/icons/DraftWaldogramsIcon';
import ShopIcon from '../../../../../../components/icons/ShopIcon';
import useRequestConfirm from '../../../../../../hooks/useRequestConfirm';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import useCloseModal from '../../../../../../hooks/useCloseModal';
import useBasename from '../../../../../../components/routing/useBasename';
import useAlbumFolderShutterbugFilterContext from '../../../AlbumFolder/AlbumFolders/AlbumFoldersShutterbugHome/hooks/useAlbumFolderShutterbugFilterContext';
import { consumerRootContent } from '../../../AlbumFolder/AlbumFolders/AlbumFoldersShutterbugHome/graphql';
import { CONTRIBUTOR_SHARE_MODAL } from '../../../../../../constants/modals';
import ContributorShare from '../../../../components/ContributorShare/ContributorShare';
import ContextualString from '../../../../../../components/contextual/ContextualString';
import { isWeddingsContent } from '../../../../../../util/album';
import {
  AlbumMembershipGroupUnjoin,
  AccountHierarchySettingsUpdate,
} from '../../../../../../graphql/mutations/hierarchy-groups';
import {
  ALBUM_TYPE,
  ALBUM_SUBTYPE,
  ALBUM_SLIDESHOW_STATUS,
} from '../../../../../../constants/albums';
import useVideoUploadPermission from '../../../../services/contributors/hooks/useVideoUploadPermission';
import GlobalWaldogramConfiguratorModal, {
  GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL,
} from '../../../AccountAddressBook/components/GlobalWaldogramConfigurator/modal';
import { albumSupportsWaldograms } from '../../../../../web-gallery/utilities/waldograms';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
    slideshow: PropTypes.any,
    joins_enabled: PropTypes.bool,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    joins_enabled: PropTypes.bool,
    root_album_folder: PropTypes.shape({
      joins_enabled: PropTypes.bool,
    }),
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
    matched_photos_extended: PropTypes.shape({
      total: PropTypes.number,
    }),
  }),
  albumMemberships: PropTypes.shape({
    records: PropTypes.arrayOf(
      PropTypes.shape({
        album: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          custom_prints_meta: PropTypes.shape({
            onsite_delivery_enabled: PropTypes.bool,
          }),
        }),
      }),
    ),
  }),
  isAlbum: PropTypes.bool,
  userId: PropTypes.string,
  hierarchyItem: PropTypes.shape({
    id: PropTypes.string,
    account_hierarchy_settings: PropTypes.shape({
      notifications_enabled: PropTypes.bool,
    }),
  }),
};

const HierarchyItemDropdown = ({
  album,
  albumFolder,
  albumMembershipGroup,
  albumMemberships,
  isAlbum,
  userId,
  hierarchyItem,
}) => {
  const requestConfirm = useRequestConfirm();
  const openAlert = useOpenAlert();
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const history = useHistory();
  const basename = useBasename();
  const hasMatches = albumMembershipGroup?.matched_photos_extended?.total > 0;
  const { filter } = useAlbumFolderShutterbugFilterContext();
  const [activeNotifications, setActiveNotifications] = useState(
    hierarchyItem?.account_hierarchy_settings?.notifications_enabled || false,
  );
  const entity = albumFolder ?? album;
  const joinsEnabled =
    entity?.joins_enabled ?? entity?.root_album_folder?.joins_enabled ?? false;

  const hasPhotoPricingPrintItems =
    albumFolder?.photo_pricing?.print_product_items?.total > 0;

  const { isAllowed: isAllowedToUploadVideo } = useVideoUploadPermission({
    entityId: entity?.id,
    checkOwnership: true,
  });

  const entityIsEvent = entity?.album_type === ALBUM_TYPE.EVENT;
  const externalUpload = entity?.enable_external_upload;
  const canUploadVideos =
    !!isAllowedToUploadVideo || (entityIsEvent && externalUpload);

  const albumsWithWaldograms = useMemo(() => {
    if (isAlbum || !albumMemberships?.records?.length) {
      return [];
    }

    return albumMemberships?.records
      ?.filter(
        ({ album: membershipAlbum }) =>
          membershipAlbum?.custom_prints_meta?.onsite_delivery_enabled,
      )
      .map(({ album: membershipAlbum }) => membershipAlbum);
  }, [isAlbum, albumMemberships]);

  const canAccessWaldograms =
    !entity?.picture_day_enabled && !albumMembershipGroup?.picture_day_enabled;

  const hasCampergrams =
    albumSupportsWaldograms(entity) || albumsWithWaldograms?.length > 0;

  const canAccessCampergrams =
    hasCampergrams || entity?.album_subtype === ALBUM_SUBTYPE.CAMPS;

  const onAlbumFolderCampergramSend = () => {
    if (albumsWithWaldograms.length === 1) {
      history.push(
        `/app/shutterbug/hierarchy-items/${albumsWithWaldograms[0].id}/postcard`,
      );
    } else {
      openAlert({
        type: 'info',
        message: ({ onRequestClose }) => (
          <div className="text-center">
            <h4>Select a session to send the card to:</h4>
            {albumsWithWaldograms.map(waldogramAlbum => (
              <Button
                key={waldogramAlbum.id}
                btnBlock
                btnStyle="link"
                onClick={() => {
                  history.push(
                    `/app/shutterbug/hierarchy-items/${waldogramAlbum.id}/postcard`,
                  );
                  onRequestClose();
                }}
              >
                {waldogramAlbum.name}
              </Button>
            ))}
          </div>
        ),
      });
    }
  };

  const hasShareAbility =
    entity?.album_type === ALBUM_TYPE.SHUTTERBUG ||
    entity?.album_type === ALBUM_TYPE.EVENT ||
    isWeddingsContent(entity);

  const inviteLabel = (
    <ContextualString
      id="inviteLabel"
      defaultMessage="Invite"
      type={entity?.album_type}
      subtype={entity?.album_subtype}
    />
  );

  const [albumMembershipGroupUnjoin, { loading }] = useMutation(
    AlbumMembershipGroupUnjoin,
    {
      update: store => {
        const { owner_status, sort, search_query } = filter;

        try {
          const queryData = store.readQuery({
            query: consumerRootContent,
            variables: {
              limit: 100,
              owner_status,
              search_query,
              sort,
              include_matched_photos_only: true,
            },
          });

          const updateData = produce(queryData, draft => {
            draft.me = {
              ...queryData.me,
              hierarchy_groups: {
                ...queryData.me.hierarchy_groups,
                total: queryData.me.hierarchy_groups.total - 1,
                records: queryData.me.hierarchy_groups.records.filter(
                  hierarchyGroup =>
                    hierarchyGroup?.album_membership_group?.id !==
                    albumMembershipGroup?.id,
                ),
              },
            };
          });

          store.writeQuery({
            query: consumerRootContent,
            variables: {
              limit: 100,
              owner_status,
              search_query,
              sort,
              include_matched_photos_only: true,
            },
            data: updateData,
          });
        } catch (error) {
          // Do nothing
        }
      },
      onCompleted: () => {
        history.replace(`/app/${basename}/folders`);
      },
      onError: () => {
        openAlert({
          type: 'error',
          title: 'Error Removing Event',
          message: 'Unable to remove the event. Please try again later.',
        });
      },
    },
  );

  const [accountHierarchySettingsUpdate, { loading: loadingNotification }] =
    useMutation(AccountHierarchySettingsUpdate, {});

  const onAccountHierarchySettingsToggle = async () => {
    if (loadingNotification) {
      return;
    }

    try {
      await accountHierarchySettingsUpdate({
        variables: {
          input: {
            hierarchy_item_id: hierarchyItem?.id,
            notifications_enabled: !activeNotifications,
          },
        },
      });
      setActiveNotifications(!activeNotifications);
    } catch {
      // Do nothing
    }
  };

  const onRemoveEvent = () => {
    const executeUnjoin = () => {
      albumMembershipGroupUnjoin({
        variables: {
          input: {
            album_membership_group_id: albumMembershipGroup?.id,
            account_id: userId,
          },
        },
      });
    };

    if (hasMatches) {
      requestConfirm({
        message: (
          <div className="mt-2">
            <p className="text-dark text-extra-bold">Are you sure?</p>
            <p>
              It looks like you have matches in this event. Are you sure you
              want to remove it?
            </p>
          </div>
        ),
        confirmText: 'Yes, Remove this event',
        confirmBtnStyle: 'danger',
        onConfirm: executeUnjoin,
        onCancel: () => {},
      });
    } else {
      requestConfirm({
        message: (
          <div className="mt-2">
            <p className="text-dark text-extra-bold">Are you sure?</p>
            <p>
              You are about to remove the event from your library. Are you sure
              you want to proceed ?
            </p>
          </div>
        ),
        confirmText: 'Yes, Remove this event',
        confirmBtnStyle: 'danger',
        onConfirm: executeUnjoin,
        onCancel: () => {},
      });
    }
  };

  return (
    <Fragment>
      <div className="dropdown-menu-dark">
        <DropdownMenu
          placement="bottom-end"
          closeOnClickWithin
          dropdownClassName="overflow-visible"
          toggle={
            <span className="navigation-breadcrumbs-item-label navigation-breadcrumbs-item-label-hellip-btn">
              <DotTrigger className="hellip-btn" />
            </span>
          }
        >
          {hasShareAbility && (
            <Button
              btnStyle="reset"
              className="dropdown-item"
              onClick={() => {
                openModal(CONTRIBUTOR_SHARE_MODAL, {
                  albumId: album?.id,
                  albumFolderId: albumFolder?.id,
                });
              }}
            >
              <InviteIcon color="#ffffff" size={16} className="mr-2" />
              {inviteLabel}
            </Button>
          )}
          <ContributeButton
            albumFolderId={albumFolder?.id}
            albumId={album?.id}
            noUsername
          >
            {({ onClick }) => (
              <Button
                btnStyle="reset"
                className="dropdown-item"
                onClick={onClick}
              >
                <UploadIcon color="#ffffff" size={16} className="mr-2" />
                Upload Photos
              </Button>
            )}
          </ContributeButton>
          {canUploadVideos && (
            <Button
              btnStyle="reset"
              className="dropdown-item"
              onClick={() => {
                if (album?.id) {
                  if (album?.is_video_album) {
                    history.push({
                      pathname: `/app/${basename}/albums/${album.id}/record-video`,
                    });
                  } else {
                    history.push({
                      pathname: `/app/${basename}/upload/video/${album?.album_folder?.id}`,
                      state: { target: album },
                    });
                  }
                } else if (albumFolder?.id) {
                  history.push({
                    pathname: `/app/${basename}/upload/video/${albumFolder.id}`,
                    state: { target: albumFolder },
                  });
                }
              }}
            >
              <UploadIcon color="#ffffff" size={16} className="mr-2" />
              Upload Videos
            </Button>
          )}
          {canAccessCampergrams && !canAccessWaldograms && (
            <Button
              btnStyle="reset"
              className="dropdown-item"
              onClick={() => {
                if (!isAlbum) {
                  onAlbumFolderCampergramSend();
                } else {
                  history.push(
                    `/app/shutterbug/hierarchy-items/${entity.id}/postcard`,
                  );
                }
              }}
            >
              <DraftWaldogramsIcon color="#ffffff" size={16} className="mr-2" />
              Send a Card
            </Button>
          )}
          {!canAccessCampergrams && canAccessWaldograms && (
            <Button
              btnStyle="reset"
              className="dropdown-item"
              onClick={() => {
                openModal(GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL);
              }}
            >
              <DraftWaldogramsIcon color="#ffffff" size={16} className="mr-2" />
              Send a Card
            </Button>
          )}
          {canAccessCampergrams && canAccessWaldograms && (
            <Fragment>
              <Button
                btnStyle="reset"
                className="dropdown-item"
                data-tip=""
                data-for="campergram-disabled"
                data-effect="solid"
                data-place="top"
                onClick={() => {
                  if (!isAlbum) {
                    onAlbumFolderCampergramSend();
                  } else {
                    history.push(
                      `/app/shutterbug/hierarchy-items/${entity.id}/postcard`,
                    );
                  }
                }}
                disabled={!hasCampergrams}
              >
                <DraftWaldogramsIcon
                  color="#ffffff"
                  size={16}
                  className="mr-2"
                />
                Send a card to your camper
                {!hasCampergrams && (
                  <ReactTooltip
                    id="campergram-disabled"
                    className="tooltip"
                    place="top"
                  >
                    Camper postcards are not
                    <br />
                    available or no longer available
                    <br />
                    due to delivery time.
                  </ReactTooltip>
                )}
              </Button>
              <Button
                btnStyle="reset"
                className="dropdown-item"
                onClick={() => {
                  openModal(GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL);
                }}
              >
                <DraftWaldogramsIcon
                  color="#ffffff"
                  size={16}
                  className="mr-2"
                />
                Send a card to someone else
              </Button>
            </Fragment>
          )}
          {!hasPhotoPricingPrintItems && (
            <Button
              btnStyle="reset"
              className="dropdown-item"
              onClick={() => history.push(`/app/${basename}/shop`)}
            >
              <ShopIcon color="#ffffff" size={16} className="mr-2" />
              Buy Prints
            </Button>
          )}
          {!!album?.id &&
            album?.slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED && (
              <Button
                btnStyle="reset"
                className="dropdown-item"
                onClick={() =>
                  history.replace(`/albums/${album?.id}/slideshow`)
                }
              >
                <SlideshowIcon color="#ffffff" size={16} className="mr-2" />
                Play Slideshow
              </Button>
            )}
          <Button
            btnStyle="reset"
            className="dropdown-item d-flex align-items-center"
            disabled={loadingNotification}
            onClick={event => {
              event.preventDefault();
              onAccountHierarchySettingsToggle();
            }}
          >
            <NotificationIcon
              color={activeNotifications ? '#ffffff' : '#888888'}
              size={16}
              className="mr-2"
            />
            {loadingNotification
              ? 'Updating Notifications...'
              : `Turn Notifications ${activeNotifications ? 'Off' : 'On'}`}
          </Button>
          <Button
            btnStyle="reset"
            className="dropdown-item"
            onClick={onRemoveEvent}
            disabled={loading}
          >
            <TrashIcon color="#ffffff" size={16} className="mr-2" />
            {loading ? 'Removing...' : 'Remove Event'}
          </Button>
          {joinsEnabled && (
            <Button
              className="dropdown-item"
              onClick={() =>
                history.push(
                  `/discover/album-folder/${
                    entity?.root_album_folder?.id ?? entity?.id
                  }/start`,
                )
              }
              btnStyle="reset"
              btnSize="sm"
            >
              <MyMatchesIcon color="#ffffff" size={16} className="mr-2" />
              Find My Photos
            </Button>
          )}
        </DropdownMenu>
      </div>
      <ContributorShare
        albumId={isAlbum ? entity?.id : undefined}
        albumFolderId={!isAlbum ? entity?.id : undefined}
      />
      <GlobalWaldogramConfiguratorModal
        onDismiss={() => {
          closeModal(GLOBAL_WALDOGRAM_CONFIGURATOR_MODAL);
        }}
      />
    </Fragment>
  );
};

HierarchyItemDropdown.propTypes = propTypes;

export default HierarchyItemDropdown;
